import { hasOptionalPropertyOfType } from "@ciaopeople/cp-shatsu/CustomTypes";
import { isArrayOf, isBoolean, isFunction, isNumber, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { FirebaseOptions } from "firebase/app";
import { AuthProviderIdentifier } from "../login/AuthProviderIdentifier.js";
import { SNHUBConfig } from "../snhub/SNHUBConfig.js";
import { BarrierDisplayMode } from "@api-commons/sdk/aaa/AAABarrierWithSource.js";

export interface CookieNamesConfig {
    session: string,
    logout: string,
    fbToken: string,
    aes: string,
    loginPreviousState: string,
    lastLoginProvider: string,
    audienceToken: string,
}

export function isCookieNamesConfig(ele: any): ele is CookieNamesConfig {
    return isObject(ele) && 
            [
                "session",
                "logout",
                "fbToken",
                "aes",
                "loginPreviousState",
                "lastLoginProvider",
                "audienceToken",
            ].reduce((carry, cur) => carry && isString(cur), true);
}


export interface CookieConfig {
    names: CookieNamesConfig,
    cookieDomain: string,
    cookieversion: number
}


export function isCookieConfig(ele: any): ele is CookieConfig {
    return isObject(ele) && isString(ele['cookieDomain']) 
            && isNumber(ele['cookieversion'])
            && isCookieNamesConfig(ele['names']);
}

export interface LoginProviderConfig {
    enabled: boolean,
    scopes: string[],
    applicationIdentifier: string,
    clientSecret?: string,
    codeLoginAllowed?: boolean
}

export function isLoginProviderConfig(ele: any): ele is LoginProviderConfig {
    return isObject(ele) && 
            isBoolean(ele['enabled']) && 
            isArrayOf(ele['scopes'], isString) && 
            isString(ele['applicationIdentifier']) && 
            hasOptionalPropertyOfType(ele, "clientSecret", isString);
}

export interface AutologinConfig {
    enabled: boolean,
    retrieveActualDataAlways: boolean,
    chaingen?: () => AuthProviderIdentifier[]
}

export function isAutologinConfig(ele: any): ele is AutologinConfig {
    return isObject(ele) && 
            isBoolean(ele['enabled']) && 
            isBoolean(ele['retrieveActualDataAlways']) && 
            hasOptionalPropertyOfType(ele, "chaingen", isFunction);
}



export interface LoginManagerConfig {
    firebase?: FirebaseOptions,
    actual_providers: Record<string, LoginProviderConfig>,
    firebaseAccountMergeEnable: boolean,
    autologin: AutologinConfig,
    revokeOnClientSideOnDeletion: boolean,
    excludeFromCta?: {
        [k in AuthProviderIdentifier]?: boolean
    },
    barrierDisplayMode: BarrierDisplayMode
}

export function isLoginManagerConfig(ele: any): ele is LoginManagerConfig {
    return isObject(ele) && 
            isObject(ele['actual_providers']) && 
            isBoolean(ele['firebaseAccountMergeEnable']) &&
            isBoolean(ele['revokeOnClientSideOnDeletion']) &&
            isAutologinConfig(ele['autologin']);
}


export interface ServicePathsMapping {
    basepath: string,
    settings: string,
    login: string,
    notifications: string,
    follow: string,
    bookmarks: string,
    cardView?: string,
    cardEdit?: string
}


export function isServicePathsMapping(ele: any): ele is ServicePathsMapping {
    return isObject(ele) && 
            isString(ele['basepath']) && 
            isString(ele['settings']) && 
            isString(ele['login']) && 
            isString(ele['notifications']) && 
            isString(ele['follow']);
}



export interface NativeAppConfig {
    // @task #11419
    disabled?: boolean,
    product: string,
    schema_intent: string,
    ios_alternative_url: string,
    android_alternative_url: string,
    dynamicLink: {
        baseUrl: string,
        apn: string,
        amv: string,
        ibi: string,
        isi: string,
        ius: string
    }
}




export interface ApplicationAccessTokenConfig {

    ios: string,
    android: string,
    web: string

}


export function isNativeAppConfig(ele: any): ele is NativeAppConfig {
    return isObject(ele) && 
            isString(ele['product']) && 
            isString(ele['schema_intent']);
}

export interface ContactsConfig {

    dataDeletionEmail: string

}

export function isContactsConfig(ele: any): ele is ContactsConfig {
    return isObject(ele) && 
            isString(ele['dataDeletionEmail']);
}

interface InactiveMembershipConfig {
    enable: false;
}
interface ActiveMembershipConfig {
    enable: true,
    debugMode?: boolean,
    idtoken_name: string,
    landingUrl: string,
    cardSupport?: boolean
    giftSupport: boolean,
    anonBuySupport?: boolean,
    freeTrialSupport?: boolean
}
export type MembershipConfig = InactiveMembershipConfig|ActiveMembershipConfig;

export interface PropertyConfig {
    version: string,
    debug: boolean;
    host: string;
    noAppAssociationHost?: string;
    domain: string;
    site: string;
    mainUrl: string;
    siteName: string;
    assetsDistro: string;
    assetsCacheBuster: string;
    serverSdkEndpoint: string;
    sdkJsServer: {
        host: string,
        domain: string,
        path: string,
        secure: boolean;
    };
    secure: boolean;
    applicationToken: ApplicationAccessTokenConfig;
    cookies: CookieConfig;
    loginManager: LoginManagerConfig;
    // parse: {
    //     login: boolean,
    //     follows: boolean,
    //     bookmarks: boolean
    // };
    support: {
        native_apps: boolean,
        bookmarks: boolean,
        newsletters: boolean
    },
    membership?: MembershipConfig,
    nativeApp: NativeAppConfig;
    webPush: {
        publicKey: string,
        vapiKey: string,
        serviceWorkerPath: string,
        serviceWorkerHost: string
    };
    snhubConfig: SNHUBConfig,
    ga4: {
        accountId: string,
        cookieDomain: string
    };
    optimize?: {
        accountId: string
    };
    servicePaths?: ServicePathsMapping,
    contacts: ContactsConfig,
    appDownloadLink: {
        "app-store"?: string,
        "google-play"?: string
    },
    links: {
        privacyPolicy: string
    }
}
